import ApplyViewModel from './ApplyViewModel';
import useAsyncEffect from 'use-async-effect';
import { Button, InputOptionBox } from '../../../../core/components/Index';
import { Steps } from './ApplyModels';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Appl } from '../../../../core/Appl';
import Helper from '../../../../core/services/Helper';

const TaxWithholdingDeclaration = observer(() => {
  const vm = Appl.Services.get<ApplyViewModel>('ApplyViewModel');
  let { memberId } = useParams();

  useAsyncEffect(async () => {
    await vm.loadStepAsync(Steps.DeclarationTaxWithholding, memberId!);
  }, []);

  const getText = (value: number | undefined) => {
    if (value === undefined || value === null || typeof (value) === 'string') {
      return "Not Specified"
    } else if (value === 0) {
      return Helper.formatCurrency(0)
    }
    else {
      return Helper.formatCurrency(value);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl">
      {/* Tax Withholding Header */}
      <div>
        <h1 className="text-32px tracking--1percent text-neutral-800 font-semibold mb-4">Tax Withholding</h1>
        <p className="text-black text-15px tracking--1percent leading-26px">
          To ensure that the correct amount of tax is withheld from your retirement benefits, please set your federal and state tax withholding preferences below.
        </p>
      </div>

      {/* Federal Tax Withholding Summary */}
      <div className="space-y-2">
        <h2 className="text-22px tracking--1percent text-black font-semibold">Federal Tax Withholding Summary</h2>
        <div />
        <p className="text-black text-15px">
          {vm.model.taxWithHolding.federalTaxWithHolding && vm.model.taxWithHolding.federalTaxWithHolding.federalTaxWithhold === "N" && <>Filing Status: <span className="font-bold">Do not withhold Federal income tax</span></>
          }
          {vm.model.taxWithHolding.federalTaxWithHolding && vm.model.taxWithHolding.federalTaxWithHolding.federalTaxWithhold === "Y" && <>Filing Status: <span className="font-bold">
            {vm.model.withholdFederalIncomeTaxStatusListItems?.find(o => o.key == vm.model.taxWithHolding.federalTaxWithHolding?.federalWithholdIncomeTaxStatus)?.value}
          </span>
          </>}
        </p>
        {vm.model.taxWithHolding.federalTaxWithHolding && vm.model.taxWithHolding.federalTaxWithHolding.federalTaxWithhold === "Y" && <>
          <p className="text-black text-15px">
            Claim Dependents:
          </p>
          <ul className="list-none pl-4 space-y-1">
            <li className="text-black text-15px">
              Enter the amount of the child tax credit and the credit for other dependents. <span className="font-bold">{getText(vm.model.taxWithHolding.federalTaxWithHolding?.federalClaimsDependant)}</span>
            </li>
          </ul>
          <p className="text-black text-15px">Other Adjustments:</p>
          <ul className="list-none pl-4 space-y-1">
            <li className="text-black text-15px">
              a. Enter other income not from jobs, such as interest or dividends. <span className="font-bold">{getText(vm.model.taxWithHolding.federalTaxWithHolding?.federalIncomeNotFromJob)}</span>
            </li>
            <li className="text-black text-15px">
              b. Enter deductions claimed other than the standard deduction. <span className="font-bold">{getText(vm.model.taxWithHolding.federalTaxWithHolding?.federalDeductionsClaimed)}</span>
            </li>
            <li className="text-black text-15px">
              c. Enter any additional tax you want withheld from each pay period. <span className="font-bold">{getText(vm.model.taxWithHolding.federalTaxWithHolding?.federalAdditionalWithold)}</span>
            </li>
          </ul>
        </>
        }
        <div />
        <Button label="Edit Federal Withholding" severity="warning" outlined rounded onClick={() => vm.navigate('tax-withholding/federal-tax-withholding')} />
      </div>

      {/* CA State Tax Withholding Summary */}
      <div className="space-y-2">
        <h2 className="text-22px tracking--1percent text-black font-semibold">CA State Tax Withholding Summary</h2>
        <p className="text-black text-15px">
          {vm.model.taxWithHolding.stateTaxWithHolding && vm.model.taxWithHolding.stateTaxWithHolding.stateTaxWithhold === "N" && <>Filing Status: <span className="font-bold">Do not withhold CA State income tax.</span></>
          }
          {vm.model.taxWithHolding.stateTaxWithHolding && vm.model.taxWithHolding.stateTaxWithHolding.stateTaxWithhold === "Y" && <>
            Filing Status: <span className="font-bold">{vm.model.withholdStateIncomeTaxStatusListItems?.find(o => o.key == vm.model.taxWithHolding.stateTaxWithHolding?.stateWithholdIncomeTaxStatus)?.value ? vm.model.withholdStateIncomeTaxStatusListItems?.find(o => o.key == vm.model.taxWithHolding.stateTaxWithHolding?.stateWithholdIncomeTaxStatus)?.value : 'Not Specified'}</span>
          </>}
          {vm.model.taxWithHolding.stateTaxWithHolding && vm.model.taxWithHolding.stateTaxWithHolding.stateTaxWithhold === "P" && <>
            Filing Status: <span className="font-bold">Withhold only <span className="font-bold">{getText(vm.model.taxWithHolding.stateTaxWithHolding?.stateAdditionalTaxToWithheldBasedOnBenefitPayment)}</span> from each benefit payment.</span>
          </>
          }
        </p>
        {vm.model.taxWithHolding.stateTaxWithHolding && vm.model.taxWithHolding.stateTaxWithHolding.stateTaxWithhold === "Y" && <> <p className="text-black text-15px">
          Allowances: <span className="font-bold">
            {vm.model.taxWithHolding.stateTaxWithHolding?.stateWithholdIncomeTaxStatus && vm.model.withholdStateIncomeTaxStatusListItems?.filter(o => o.key == vm.model.taxWithHolding.stateTaxWithHolding?.stateWithholdIncomeTaxStatus)?.map((status, index) => (
              <>
                {status.key === "M" &&
                  vm.model.taxWithHolding.stateTaxWithHolding?.stateMarriedWithholdIncomeAllowance
                }
                {status.key === "S" &&
                  vm.model.taxWithHolding.stateTaxWithHolding?.stateSingleWithholdIncomeAllowance
                }
                {status.key === "H" &&
                  vm.model.taxWithHolding.stateTaxWithHolding?.stateHeadOfHouseHoldIncomeAllowance
                }
              </>
            ))}
            {!vm.model.taxWithHolding.stateTaxWithHolding?.stateWithholdIncomeTaxStatus && <>Not Specified
            </>}
          </span>
        </p>
          <p className="text-black text-15px">
            Additional Withholding: <span className="font-bold">{getText(vm.model.taxWithHolding.stateTaxWithHolding?.stateAdditionalTaxToWithheldBasedOnTax)}</span>
          </p>
        </>}
        <div />
        <Button label="Edit CA State Withholding" severity="warning" outlined rounded onClick={() => vm.navigate('tax-withholding/state-tax-withholding')} />
      </div>

      {/* Declaration Section */}
      <div className="space-y-4">
        <h2 className="text-22px tracking--1percent text-black font-semibold">Tax Withholding Declaration</h2>
        <div className="flex items-start space-x-2">
          <InputOptionBox
            inputId="taxwithholdDecleration"
            value={vm.model.taxWithHolding.taxwithholdDecleration}
            checked={vm.model.taxWithHolding.taxwithholdDecleration}
            label={"I have reviewed the information on this form and submit this statement of income tax withholding on my retirement allowance. I understand this election will remain in effect until I change it. I understand that I may be responsible for any tax liability and/or penalties if my withholding and estimated tax payments are not sufficient."}
            onChange={(e: any) => vm.onInputChange(e, 'taxwithholdDecleration', vm.model.taxWithHolding)}
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="flex space-x-4 mt-6">
        <Button
          onClick={async () => await vm.onTaxWithholdingMemberDeclarationCompletedAsync(true)}
          title={`${vm.navigatedFromReviewScreen ? 'Save & Return to Review' : 'Save & Continue'}`}
          rounded
          disabled={!vm.model.taxWithHolding.taxwithholdDecleration}
          className="bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600"
        />
      </div>
    </div >
  );
});

export default TaxWithholdingDeclaration;
